/**
 * mondayApi — the ONE place the frontend talks GraphQL to monday.
 *
 * SDK: SeamlessApiClient from @mondaydotcomorg/api, the client monday recommends
 * for view apps. monday-sdk-js stays in App.js for get / listen / execute, which
 * the new package does not cover. "Seamless" means no token: the monday host
 * authenticates the iframe's requests as the viewing user.
 *
 * Contract — identical to the old `monday.api()` on purpose, so no call site or
 * test fake changed: resolves `{ data }` or `{ data, errors }` and NEVER rejects
 * on a GraphQL error. monday answers a refused query (missing scope, bad value)
 * with HTTP 200 + `errors[]`; SeamlessApiClient turns that into a thrown
 * SeamlessApiClientError, and this wrapper turns it back into the envelope,
 * because every caller inspects the envelope (a missing scope must not look like
 * an empty result). A timeout, or running outside monday, still rejects.
 */
import { SeamlessApiClient } from '@mondaydotcomorg/api';

/**
 * The API version every call pins. 2026-01 serves Column.settings (2025-10+),
 * which every board read uses; its only breaking changes since 2025-04 are
 * error formats, which lib/mondayBatch already matches. Change it here and
 * nowhere else.
 */
export const API_VERSION = '2026-01';

// Created on first use, not at import: App.test.js loads this module outside
// monday, and the client registers a window message listener when built.
let client = null;
function getClient() {
  if (!client) client = new SeamlessApiClient(API_VERSION);
  return client;
}

/**
 * Despite its `Promise<T>` typing, SeamlessApiClient.request resolves with the
 * host's whole postMessage reply — `{ requestId, data: <response body> }` —
 * and the body is the usual `{ data, errors?, account_id }`. monday-sdk-js did
 * this unwrap itself (`.then(result => result.data)`); here it is ours. Taking
 * the reply as the data made every read look empty (no columns, no subitems
 * column) while errors, which go through the thrown path, still came through.
 */
function envelopeOf(reply) {
  const body = (reply && reply.data) || {};
  return body.errors && body.errors.length ? { data: body.data, errors: body.errors } : { data: body.data };
}

/**
 * api(query, { variables?, apiVersion? }) → { data } | { data, errors }
 *
 * `apiVersion` overrides API_VERSION for this one call (a feature that needs a
 * newer version than the pin, e.g. mentions in create_update from 2025-07).
 */
export async function api(query, { variables, apiVersion } = {}) {
  const options = apiVersion ? { versionOverride: apiVersion } : undefined;
  try {
    const reply = await getClient().request(query, variables, options);
    return envelopeOf(reply);
  } catch (e) {
    if (e && e.response && e.response.errors) {
      return { data: e.response.data, errors: e.response.errors };
    }
    throw e;
  }
}

const mondayApi = { api };
export default mondayApi;
